import {ReactNode, SyntheticEvent, useCallback} from 'react';
import {twMerge} from '../common/twMerge';
import {Icon} from '../design-system/atoms/Icon/Icon';
import {IconType} from '../design-system/atoms/Icon/IconType.enum';
import {InfinityScroll} from '../design-system/atoms/InfinityScroll/InfinityScroll';
import {Loading} from '../design-system/atoms/Loading/Loading';
import {ReactQueryInfiniteType} from './CodegenReactQueryType.type';

interface Props {
  type?: ReactQueryInfiniteType;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  fetchNextPage: VoidFunction;
  fetchPreviousPage: VoidFunction;
  onStartFetchNextPage?: (e: SyntheticEvent<HTMLDivElement>) => void;
  onStartFetchPrevPage?: (e: SyntheticEvent<HTMLDivElement>) => void;
  children: ReactNode;
}

export function ReactQueryInfiniteLoader({
  type = ReactQueryInfiniteType.InfinityScroll,
  isFetching,
  isFetchingNextPage,
  hasNextPage,
  hasPreviousPage,
  fetchNextPage,
  fetchPreviousPage,
  onStartFetchNextPage,
  onStartFetchPrevPage,
  children,
}: Props) {
  const isInfiniteScroll = type === ReactQueryInfiniteType.InfinityScroll;
  const isArrowBoth = type === ReactQueryInfiniteType.ArrowBoth;
  const isArrowUp = type === ReactQueryInfiniteType.ArrowUp;
  const isArrowDown = type === ReactQueryInfiniteType.ArrowDown;
  const showMoreIcon = isArrowBoth || isArrowUp || isArrowDown;

  const handlePrevious = useCallback(
    (e: SyntheticEvent<HTMLDivElement>) => {
      if (onStartFetchPrevPage) {
        onStartFetchPrevPage(e);
        fetchPreviousPage();
      }
    },
    [onStartFetchPrevPage, fetchPreviousPage]
  );

  const handleNext = useCallback(
    (e: SyntheticEvent<HTMLDivElement>) => {
      if (onStartFetchNextPage) {
        onStartFetchNextPage(e);
        fetchNextPage();
      }
    },
    [onStartFetchNextPage, fetchNextPage]
  );

  if (isInfiniteScroll) {
    return (
      <InfinityScroll
        isLoading={isFetching}
        canLoadNextPage={hasNextPage}
        requestNextPageData={fetchNextPage}
      >
        {children}
        {isFetching && isFetchingNextPage && (
          <div className="flex-center-full">
            <Loading />
          </div>
        )}
      </InfinityScroll>
    );
  }

  return (
    <>
      {hasPreviousPage && showMoreIcon && (
        <div
          className={twMerge(
            'flex items-center justify-center bg-theme-bg-surface-alt',
            'cursor-pointer',
            'mx-15pxr py-8pxr',
            'border-t-1 border-solid border-theme-transparent-20'
          )}
          onClick={handlePrevious}
        >
          <Icon type={IconType.ListArrowUp} />
        </div>
      )}
      {children}
      {hasNextPage && showMoreIcon && (
        <div
          className="flex cursor-pointer items-center justify-center rounded-b-12pxr bg-theme-bg-surface-alt py-8pxr"
          onClick={handleNext}
        >
          <Icon type={IconType.ListArrowDown} />
        </div>
      )}
    </>
  );
}
